/**
 * Plan vs actual for one register item — stage gates for Engineering,
 * Procurement and Pre-commissioning, quantity (DPR) for Fabrication &
 * Construction.
 */
import { Badge } from '@/components/ui/badge';

const pct = (v) => (v == null ? '—' : `${Math.round(v * 10) / 10}%`);
const qty = (v, u) => (v == null ? '—' : `${Math.round(v * 100) / 100}${u ? ` ${u}` : ''}`);
const day = (v) => (v ? String(v).slice(0, 10) : '—');

const varColour = (v) => (v == null ? 'text-muted-foreground'
  : v < -5 ? 'text-rose-500' : v > 5 ? 'text-emerald-500' : 'text-amber-500');

export function PlanActualTiles({ pva, measure }) {
  const tiles = measure === 'QUANTITY'
    ? [['Plan to date', qty(pva?.planQty, pva?.uom)], ['Actual to date', qty(pva?.actualQty, pva?.uom)],
       ['Variance', qty(pva?.varianceQty, pva?.uom)], ['Scope', qty(pva?.scopeQuantity, pva?.uom)]]
    : [['Planned', pct(pva?.plannedPct)], ['Forecast', pct(pva?.forecastPct)],
       ['Actual', pct(pva?.actualPct)], ['Variance', pct(pva?.variancePct)]];
  return (
    <div className="grid grid-cols-4 gap-1.5" data-testid="disc-plan-actual-tiles">
      {tiles.map(([k, v], i) => (
        <div key={k} className="rounded-md border p-2">
          <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{k}</div>
          <div className={`text-xs font-semibold ${i === 3 ? varColour(pva?.variancePct) : ''}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

export function StageGateTable({ gates }) {
  if (!gates?.length) {
    return <p className="text-[11px] text-muted-foreground px-1">This item has no stage gates configured.</p>;
  }
  return (
    <table className="w-full text-[11px]" data-testid="disc-gate-table">
      <thead className="text-muted-foreground uppercase text-[9px]">
        <tr>{['Gate', 'Weight', 'Planned', 'Forecast', 'Actual', 'Progress'].map(h => (
          <th key={h} className="text-left px-1.5 py-1 font-medium">{h}</th>))}</tr>
      </thead>
      <tbody>
        {gates.map((g, i) => (
          <tr key={`${g.name}-${i}`} className="border-t border-border" data-testid={`disc-gate-${g.name}`}>
            <td className="px-1.5 py-1 text-foreground">{g.name}</td>
            <td className="px-1.5 py-1 text-muted-foreground">{g.weight ?? '—'}</td>
            <td className="px-1.5 py-1 text-muted-foreground">{day(g.plannedDate)}</td>
            <td className="px-1.5 py-1 text-muted-foreground">{day(g.forecastDate)}</td>
            <td className="px-1.5 py-1 text-muted-foreground">{day(g.actualDate)}</td>
            <td className="px-1.5 py-1">
              <Badge variant="outline" className="text-[9px] h-4 px-1">{pct(g.progress)}</Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function QuantityPanel({ pva }) {
  const rows = [
    ['Scope quantity', qty(pva?.scopeQuantity, pva?.uom)],
    ['Plan to date', qty(pva?.planQty, pva?.uom)],
    ['Forecast to date', qty(pva?.forecastQty, pva?.uom)],
    ['Actual to date', qty(pva?.actualQty, pva?.uom)],
    ['Variance', qty(pva?.varianceQty, pva?.uom)],
    ['Remaining', qty(pva?.remainingQty, pva?.uom)],
    ['Quantity SPI', pva?.spiQuantity == null ? '—' : pva.spiQuantity],
  ];
  return (
    <table className="w-full text-[11px]" data-testid="disc-qty-table">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-t border-border">
            <td className="px-1.5 py-1 text-muted-foreground">{k}</td>
            <td className="px-1.5 py-1 text-foreground text-right font-medium">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function PlanActualDetail({ measure, pva, gates }) {
  return (
    <div className="space-y-2">
      <PlanActualTiles pva={pva} measure={measure} />
      {measure === 'QUANTITY' ? <QuantityPanel pva={pva} /> : <StageGateTable gates={gates} />}
    </div>
  );
}
